'use client'

import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, ChevronUp, CircleAlert } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

const SelectDefault = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

export const selectTriggerVariants = cva(
  'flex w-full items-center justify-between rounded-full border border-neutral-300 bg-background px-3  focus:outline-none disabled:cursor-not-allowed hover:border-neutral-100 data-[state="open"]:bg-neutral-100 data-[state="open"]:border-neutral-100 disabled:opacity-50 [&>span]:line-clamp-1 text-sm leading-4 text-neutral-600 hover:bg-neutral-100 data-[placeholder]:text-neutral-300 group data-[placeholder]:text-sm data-[placeholder]:font-normal',
  {
    variants: {
      size: {
        default: 'h-11 px-6 py-3.5',
        small: 'h-8 py-2 px-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, children, size, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ size }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className='ease h-5 w-5 text-neutral-500 transition-transform group-data-[state=open]:rotate-180' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className='h-4 w-4' />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className='h-4 w-4' />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-2xl bg-popover text-popover-foreground shadow-[0px_1px_4px_0px_hsla(0,0%,0%,0.08),_0px_0px_24px_0px_hsla(0,0%,0%,0.06)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center gap-1 rounded-xl px-4 py-2 text-sm leading-4 text-neutral-500 outline-none hover:bg-neutral-100 hover:text-neutral-500 data-[disabled]:pointer-events-none data-[state=checked]:bg-neutral-100 data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

type Props = {
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  options: { value: string; label: React.ReactNode }[]
  className?: {
    trigger?: string
    content?: string
    item?: string
  }
  disabled?: boolean
  defaultValue?: string
  readonly?: boolean
  error?: { message?: string } | boolean
  renderValue?: (
    option: { value: string; label: React.ReactNode } | undefined
  ) => React.ReactNode
}

const SelectWrapper = ({
  value,
  onValueChange,
  placeholder,
  options,
  className,
  disabled,
  readonly,
  error,
  renderValue,
  defaultValue,
  ...props
}: Props) => {
  const errorMessage = typeof error === 'object' ? error?.message : undefined

  const onChange = (value: string) => {
    onValueChange?.(value)
  }

  const selectedOption = options.find((option) => option.value === value)

  const displayValue = renderValue ? renderValue(selectedOption) : undefined

  return (
    <SelectDefault
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      defaultValue={defaultValue}
    >
      <div className='relative'>
        <SelectTrigger
          className={cn(
            className?.trigger,
            (readonly || disabled) && 'pointer-events-none',
            errorMessage && 'border-error-500'
          )}
          disabled={disabled}
          {...props}
        >
          <SelectValue
            placeholder={placeholder}
            defaultValue={defaultValue}
            className='placeholder:text-neutral-300'
          >
            {displayValue}
          </SelectValue>
        </SelectTrigger>

        {errorMessage && (
          <span className='ml-2 mt-1 flex items-center'>
            <CircleAlert className='mr-1 h-3.5 w-3.5 fill-error-500 stroke-error-500 [&_line]:stroke-light' />
            <p className={cn('font-sans text-xs leading-4 text-error-500')}>
              {errorMessage}
            </p>
          </span>
        )}
      </div>
      <SelectContent className={className?.content}>
        {options?.map((option, index) => (
          <SelectItem
            key={index}
            value={option.value}
            className={className?.item}
            disabled={disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectDefault>
  )
}

const Select = SelectWrapper

export {
  Select,
  SelectContent,
  SelectDefault,
  SelectGroup,
  SelectItem,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
